import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getSettings, updateBrandSettings, updateProfile, changePassword } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    Settings as SettingsIcon,
    Store,
    User,
    Save,
    Loader2,
    CheckCircle,
    XCircle,
    Link,
    FileSpreadsheet,
    Lock,
    Eye,
    EyeOff,
    Plus,
    Trash2
} from 'lucide-react';

const Settings = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(isAdmin ? 'store' : 'profile');

    // Toast state
    const [toast, setToast] = useState(null);

    // Store settings state
    const [brand, setBrand] = useState({
        name: '',
        slug: '',
        sheetUrl: '',
        sheetName: '',
        additionalSheets: [],
        active: true
    });

    // Profile settings state
    const [profile, setProfile] = useState({
        name: '',
        email: ''
    });

    // Password change state
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });



    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getSettings();
            if (res.success) {
                setBrand(res.data.brand);
                setProfile({
                    name: res.data.user.name,
                    email: res.data.user.email
                });
            }
        } catch (err) {
            showToast('error', err.response?.data?.message || t('settings.messages.fetch_failed') || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 4000);
    };

    const handleSaveBrand = async () => {
        try {
            setSaving(true);
            const res = await updateBrandSettings({
                name: brand.name,
                sheetUrl: brand.sheetUrl,
                sheetName: brand.sheetName,
                additionalSheets: brand.additionalSheets || []
            });
            if (res.success) {
                setBrand(res.data);
                showToast('success', t('settings.messages.save_success') || 'Store settings saved successfully!');
            }
        } catch (err) {
            showToast('error', err.response?.data?.message || t('settings.messages.save_failed') || 'Failed to save store settings');
        } finally {
            setSaving(false);
        }
    };

    const handleAddSheet = () => {
        setBrand(prev => ({
            ...prev,
            additionalSheets: [...(prev.additionalSheets || []), { sheetUrl: '', sheetName: 'Youcan-Orders' }]
        }));
    };

    const handleRemoveSheet = (index) => {
        setBrand(prev => ({
            ...prev,
            additionalSheets: (prev.additionalSheets || []).filter((_, i) => i !== index)
        }));
    };

    const handleSheetChange = (index, field, value) => {
        setBrand(prev => {
            const updated = [...(prev.additionalSheets || [])];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, additionalSheets: updated };
        });
    };

    const handleSaveProfile = async () => {
        try {
            setSaving(true);
            const res = await updateProfile(profile);
            if (res.success) {
                setProfile({
                    name: res.data.name,
                    email: res.data.email
                });
                showToast('success', t('settings.messages.save_success') || 'Profile updated successfully!');
            }
        } catch (err) {
            showToast('error', err.response?.data?.message || t('settings.messages.save_failed') || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            showToast('error', t('settings.messages.password_mismatch') || 'New passwords do not match');
            return;
        }
        try {
            setSaving(true);
            const res = await changePassword(passwordData);
            if (res.success) {
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                showToast('success', t('settings.messages.save_success') || 'Password changed successfully!');
            }
        } catch (err) {
            showToast('error', err.response?.data?.message || t('settings.messages.save_failed') || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 max-w-4xl mx-auto flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 size={40} className="text-indigo-500 animate-spin mb-4" />
                <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('settings.loading')}</p>
            </div>
        );
    }

    return (
        <div className="px-4 py-6 md:p-8 w-full max-w-4xl mx-auto space-y-6 md:space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight flex items-center gap-2.5 md:gap-3">
                    <SettingsIcon size={24} className="text-indigo-500 md:w-7 md:h-7" />
                    {t('settings.title')}
                </h1>
                <p className="text-xs md:text-sm text-gray-500 font-medium mt-1">{t('settings.subtitle')}</p>
            </div>

            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-2xl shadow-lg border ${toast.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                    {toast.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                    <span className="font-bold text-sm">{toast.message}</span>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 bg-gray-100 p-1.5 rounded-2xl w-full sm:w-fit overflow-x-auto no-scrollbar">

                {isAdmin && (
                    <button
                        onClick={() => setActiveTab('store')}
                        className={`flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-bold text-xs md:text-sm transition-all whitespace-nowrap shrink-0 ${activeTab === 'store'
                            ? 'bg-white text-indigo-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Store size={16} className="md:w-[18px] md:h-[18px]" />
                        {t('settings.tabs.store')}
                    </button>
                )}
                <button
                    onClick={() => setActiveTab('profile')}
                    className={`flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-bold text-xs md:text-sm transition-all whitespace-nowrap shrink-0 ${activeTab === 'profile'
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <User size={16} className="md:w-[18px] md:h-[18px]" />
                    {t('settings.tabs.profile')}
                </button>
            </div>



            {/* Store Settings Tab */}
            {activeTab === 'store' && isAdmin && (
                <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-6 md:p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-6 border-b border-gray-50">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-500">
                            <Store size={20} className="md:w-6 md:h-6" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-base md:text-lg font-black text-gray-900 truncate">{t('settings.store.title')}</h2>
                            <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">{t('settings.store.brand_slug', { slug: brand.slug })}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">{t('settings.store.name')}</label>
                            <input
                                type="text"
                                name="x-store-name"
                                autoComplete="off"
                                className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold text-sm md:text-base"
                                value={brand.name}
                                onChange={(e) => setBrand({ ...brand, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">
                                {t('settings.store.status')}
                            </label>
                            <div className={`px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border ${brand.active ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'} font-bold text-sm md:text-base`}>
                                {brand.active ? t('settings.store.active') : t('settings.store.inactive')}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6 pt-4">
                        <div>
                            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2 flex items-center gap-2">
                                <Link size={14} />
                                {t('settings.store.sheet_url')}
                            </label>
                            <input
                                type="url"
                                name="x-sheet-url"
                                autoComplete="off"
                                placeholder="https://docs.google.com/spreadsheets/d/..."
                                className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium text-xs md:text-sm"
                                value={brand.sheetUrl}
                                onChange={(e) => setBrand({ ...brand, sheetUrl: e.target.value })}
                            />
                            <p className="text-[9px] md:text-[10px] text-gray-400 mt-1.5 md:mt-2 font-medium">{t('settings.store.sheet_url_hint')}</p>
                        </div>

                        <div>
                            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2 flex items-center gap-2">
                                <FileSpreadsheet size={14} />
                                {t('settings.store.sheet_name')}
                            </label>
                            <input
                                type="text"
                                name="x-sheet-name"
                                autoComplete="off"
                                placeholder="Youcan-Orders"
                                className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold text-sm md:text-base"
                                value={brand.sheetName}
                                onChange={(e) => setBrand({ ...brand, sheetName: e.target.value })}
                            />
                            <p className="text-[9px] md:text-[10px] text-gray-400 mt-1.5 md:mt-2 font-medium">{t('settings.store.sheet_name_hint')}</p>
                        </div>
                    </div>

                    {/* Additional Sheets Section */}
                    <div className="pt-6 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-xs md:text-sm font-black text-gray-700 flex items-center gap-2">
                                    <FileSpreadsheet size={16} className="text-indigo-400" />
                                    {t('settings.store.additional_sheets')}
                                </h3>
                                <p className="text-[9px] md:text-[10px] text-gray-400 mt-0.5 font-medium">{t('settings.store.additional_sheets_hint')}</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleAddSheet}
                                className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 rounded-lg md:rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 font-bold text-[10px] md:text-xs transition-all active:scale-95"
                            >
                                <Plus size={14} />
                                {t('settings.store.add_sheet')}
                            </button>
                        </div>

                        {(brand.additionalSheets || []).length === 0 ? (
                            <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                <FileSpreadsheet size={24} className="mx-auto text-gray-300 mb-2" />
                                <p className="text-[10px] md:text-xs text-gray-400 font-medium">{t('settings.store.additional_sheets_hint')}</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {(brand.additionalSheets || []).map((sheet, index) => (
                                    <div key={index} className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-5 border border-gray-100 space-y-3 relative group">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] md:text-xs font-black text-indigo-500 uppercase tracking-widest">
                                                {t('settings.store.sheet_number', { number: index + 2 })}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSheet(index)}
                                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 font-bold text-[10px] md:text-xs transition-all"
                                            >
                                                <Trash2 size={12} />
                                                {t('settings.store.remove_sheet')}
                                            </button>
                                        </div>
                                        <div>
                                            <label className="block text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                                                <Link size={12} />
                                                {t('settings.store.sheet_url')}
                                            </label>
                                            <input
                                                type="url"
                                                placeholder="https://docs.google.com/spreadsheets/d/..."
                                                className="w-full px-3 py-2 md:px-3.5 md:py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium text-xs"
                                                value={sheet.sheetUrl}
                                                onChange={(e) => handleSheetChange(index, 'sheetUrl', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                                                <FileSpreadsheet size={12} />
                                                {t('settings.store.sheet_name')}
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Youcan-Orders"
                                                className="w-full px-3 py-2 md:px-3.5 md:py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold text-xs md:text-sm"
                                                value={sheet.sheetName}
                                                onChange={(e) => handleSheetChange(index, 'sheetName', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="pt-6 border-t border-gray-50">
                        <button
                            onClick={handleSaveBrand}
                            disabled={saving}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 md:px-6 md:py-3.5 rounded-lg md:rounded-xl font-bold transition-all shadow-lg shadow-indigo-200 disabled:opacity-50 active:scale-95 text-sm md:text-base"
                        >
                            {saving ? <Loader2 size={16} className="animate-spin md:w-[18px] md:h-[18px]" /> : <Save size={16} className="md:w-[18px] md:h-[18px]" />}
                            {t('settings.store.save_button')}
                        </button>
                    </div>
                </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
                <div className="space-y-6">
                    {/* Profile Information Card */}
                    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-6 md:p-8 space-y-6">
                        <div className="flex items-center gap-3 pb-6 border-b border-gray-50">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-500">
                                <User size={20} className="md:w-6 md:h-6" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base md:text-lg font-black text-gray-900 truncate">{t('settings.profile.title')}</h2>
                                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">{t('settings.profile.role', { role: user?.role })}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">{t('settings.profile.name')}</label>
                                <input
                                    type="text"
                                    name="x-profile-name"
                                    autoComplete="off"
                                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold text-sm md:text-base"
                                    value={profile.name}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">{t('settings.profile.email')}</label>
                                <input
                                    type="email"
                                    name="x-profile-email"
                                    autoComplete="off"
                                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold text-sm md:text-base"
                                    value={profile.email}
                                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-50">
                            <button
                                onClick={handleSaveProfile}
                                disabled={saving}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 md:px-6 md:py-3.5 rounded-lg md:rounded-xl font-bold transition-all shadow-lg shadow-blue-200 disabled:opacity-50 active:scale-95 text-sm md:text-base"
                            >
                                {saving ? <Loader2 size={16} className="animate-spin md:w-[18px] md:h-[18px]" /> : <Save size={16} className="md:w-[18px] md:h-[18px]" />}
                                {t('settings.profile.save_button')}
                            </button>
                        </div>
                    </div>

                    {/* Password Change Card */}
                    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-6 md:p-8 space-y-6">
                        <div className="flex items-center gap-3 pb-6 border-b border-gray-50">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-500">
                                <Lock size={20} className="md:w-6 md:h-6" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base md:text-lg font-black text-gray-900 truncate">{t('settings.profile.password_title')}</h2>
                                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">{t('settings.profile.password_subtitle')}</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="relative">
                                <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">{t('settings.profile.current_password')}</label>
                                <input
                                    type={showPasswords.current ? "text" : "password"}
                                    name="x-current-pass"
                                    autoComplete="new-password"
                                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold pr-12 text-sm md:text-base"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                    className="absolute right-4 top-[34px] md:top-9 text-gray-400 hover:text-gray-600"
                                >
                                    {showPasswords.current ? <EyeOff size={16} className="md:w-[18px] md:h-[18px]" /> : <Eye size={16} className="md:w-[18px] md:h-[18px]" />}
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                    <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">{t('settings.profile.new_password')}</label>
                                    <input
                                        type={showPasswords.new ? "text" : "password"}
                                        name="x-new-pass"
                                        autoComplete="new-password"
                                        className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold pr-12 text-sm md:text-base"
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                        className="absolute right-4 top-[34px] md:top-9 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.new ? <EyeOff size={16} className="md:w-[18px] md:h-[18px]" /> : <Eye size={16} className="md:w-[18px] md:h-[18px]" />}
                                    </button>
                                </div>
                                <div className="relative">
                                    <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2">{t('settings.profile.confirm_password')}</label>
                                    <input
                                        type={showPasswords.confirm ? "text" : "password"}
                                        name="x-confirm-pass"
                                        autoComplete="new-password"
                                        className="w-full px-3.5 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold pr-12 text-sm md:text-base"
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                        className="absolute right-4 top-[34px] md:top-9 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPasswords.confirm ? <EyeOff size={16} className="md:w-[18px] md:h-[18px]" /> : <Eye size={16} className="md:w-[18px] md:h-[18px]" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-50">
                            <button
                                onClick={handleChangePassword}
                                disabled={saving || !passwordData.currentPassword || !passwordData.newPassword}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-3 md:px-6 md:py-3.5 rounded-lg md:rounded-xl font-bold transition-all shadow-lg shadow-amber-200 disabled:opacity-50 active:scale-95 text-sm md:text-base"
                            >
                                {saving ? <Loader2 size={16} className="animate-spin md:w-[18px] md:h-[18px]" /> : <Lock size={16} className="md:w-[18px] md:h-[18px]" />}
                                {t('settings.profile.change_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
